/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-color-literals */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO } from 'date-fns';
import Toast from 'react-native-toast-message';

import { filterAdded, paramsReseted } from '@/store/params';
import { showPopup } from '@/store/modal';

import * as Layout from '@components/screen/Layout';
import { Container } from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import { NoTranslate } from '@components/shared/TranslationContext';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import Button from '@components/form/Button';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import ModalLoyaltySetup from '@components/customer/loyalty/ModalLoyaltySetup';
import ModalViewLoyalty from '@components/customer/loyalty/ModalViewLoyalty';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import Image from '@components/global/Image';
import ModalPricelistUpload from '@components/setting/pricelist/ModalPricelistUpload';
import ModalUpload from '@components/modal/ModalUpload';

import Colors from '@global/colors';
import { isWeb, isMobileWeb } from '@global/screens';
import { buttonPrimary, buttonRounded } from '@/assets/styles/buttonStyles';
import { exportCsv } from '@api/ms-b2b';
import { generateStatus, getStatus, getErrorMessage } from '@global/helpers';
import { uploadProductPriceRules } from '@api/ms-catalog';
import importTemplate from '@/assets/csv/template-import-pricelist';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetPricelistListQuery,
  useGetPricelistCountQuery,
  useGetPricelistInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  description: { head: 'Description', body: 'description' },
  effective_date: {
    head: 'Effective Date',
    body: 'effective_date',
  },
  last_updated: { head: 'Last Update', body: 'last_updated' },
  status: { head: 'Status', body: 'status', type: 'slot' },
  is_active_auto: {
    head: 'Is Active',
    body: 'is_active_auto',
    type: 'status_active',
  },
};

const CHANNEL_OPTIONS = [
  { label: 'GT', value: 'GT' },
  { label: 'MT', value: 'MT' },
];

const ADD_ROLES = ['superadmin', 'sales_admin', 'marketing_admin', 'general'];

// UI filter shape → API query filter (was the inline transform in getTableData):
//  - description → { $regex, $options }
//  - effective-date range (from_date $gte / to_date $lte), status,
//    market_type.code (channel) pass through
const toApiFilter = (raw = {}) => {
  const filter = {};
  const desc = raw.description?.$regex ?? raw.description;
  if (desc) {
    filter.description = { $regex: desc, $options: 'i' };
  }
  if (raw.from_date) {
    filter.from_date = raw.from_date;
  }
  if (raw.to_date) {
    filter.to_date = raw.to_date;
  }
  if (raw.status) {
    filter.status = raw.status;
  }
  if (raw['market_type.code']) {
    filter['market_type.code'] = raw['market_type.code'];
  }
  return filter;
};

// Display-formatted row (RTK cache is frozen → new object). Status keeps the
// price-rules-specific label/color from generateStatus (StatusBadge's generic
// map doesn't cover price-rule statuses).
const mapRow = (pr) => {
  const status = generateStatus('price_rules', pr.status, 'value');
  let effective_date = '-';
  let last_updated = '-';
  if (pr.from_date && pr.to_date) {
    effective_date =
      format(parseISO(pr.from_date), 'dd MMM yyyy') +
      ' - ' +
      format(parseISO(pr.to_date), 'dd MMM yyyy');
  }
  if (pr.updated_at) {
    last_updated = format(parseISO(pr.updated_at), 'dd MMM yyyy');
  }
  return {
    ...pr,
    id: pr._id,
    last_updated,
    effective_date,
    status: status.label,
    statusColor: status.color,
  };
};

function PricelistScreen(props) {
  // ── selectors ──
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const showFilter = useSelector((state) => state.modal.showFilter);
  const pricelistUpload = useSelector((state) => state.modal.pricelistUpload);
  const showImportModalPricelist = useSelector(
    (state) => state.modal.showImportModalPricelist,
  );

  // ── local state ──
  const [id, setId] = useState(null);
  const [modalLoyalty, setModalLoyalty] = useState(false);
  const [modalView, setModalView] = useState(false);
  const [isAddMode, setIsAddMode] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [draftFilter, setDraftFilter] = useState({});

  // ── derived ──
  const showAddEdit = useMemo(
    () => ADD_ROLES.indexOf(auth.user?.role?.code || '') > -1,
    [auth],
  );

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetPricelistListQuery,
    countHook: useGetPricelistCountQuery,
    infiniteHook: useGetPricelistInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.description;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── handlers ──
  const callBackRowPress = (item) => {
    const rowId = item?.item?._id || '';
    const name = item?.item?.description || '';
    if (rowId && name) {
      openChild('PricelistEditScreen', { id: rowId, name });
    }
  };

  const showDetail = () => {
    dispatch(showPopup({ name: 'pricelistUpload', value: true }));
  };

  const onClose = (reload = false) => {
    setIsAddMode(true);
    setId(null);
    setModalLoyalty(false);
    if (reload) {
      dispatch(paramsReseted({ key: scope }));
    }
  };

  const modalViewLoyalty = (item) => {
    if (item) {
      setSelectedRow(item);
      setModalView(true);
    } else {
      setSelectedRow(null);
      setModalView(false);
    }
  };

  const onEdit = (editId) => {
    modalViewLoyalty(false);
    setId(editId);
    setIsAddMode(false);
    setModalLoyalty(true);
  };

  const exportPriceLogs = () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    exportCsv('product-price-rules', {})
      .then((res) => {
        if (res.data.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Export in progress, the result will be sent to your email',
            type: 'success',
          });
        }
      })
      .catch((e) => {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({ text1: getErrorMessage(e), type: 'error' });
      });
  };

  const callback = (data) => {
    if (data.key === 'add') {
      showDetail('add');
    } else if (data.key === 'filter') {
      dispatch(showPopup({ name: 'showFilter', value: 'pricelist-screen' }));
    } else if (data.key === 'export') {
      exportPriceLogs();
    }
  };

  const handleImport = async (url) => {
    const failedProcess = (error) => {
      Toast.show({
        text1: error?.response?.data?.message
          ? error.response.data.message
          : 'A system error occurred',
        type: 'error',
      });
    };
    try {
      const response = await uploadProductPriceRules({ url });
      if (response.status == 200) {
        Toast.show({
          text1: 'Bulk Create Product Pricelist is in progress',
          type: 'success',
        });
        props.navigation.replace('PricelistScreen');
      } else {
        failedProcess();
      }
    } catch (error) {
      failedProcess(error);
    } finally {
      dispatch(showPopup({ name: 'showImportModalPricelist', value: false }));
    }
  };

  const renderItem = ({ data: item, index }) => (
    <TouchableOpacity
      onPress={() => callBackRowPress({ item, index })}
      key={'list-price-' + index}
      style={styles.tableRow}>
      <View style={{ flexDirection: 'row', width: '100%' }}>
        <View style={{ flex: 2 }}>
          <SociollaText
            type="medium"
            style={{
              paddingBottom: 9,
              fontSize: 16,
              color: Colors.black1,
              lineHeight: 24,
            }}>
            {item?.is_default && (
              <View style={styles.badgeContainer}>
                <SociollaText style={styles.badgeText}>Default</SociollaText>
              </View>
            )}
            {item.description}
          </SociollaText>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image
              style={{ width: 16, height: 16, marginRight: 8 }}
              width={16}
              height={16}
              svg={true}
              source={require('@assets/img/icons/menu/calendar.svg')}
            />
            <SociollaText style={{ fontSize: 12, color: Colors.black2 }}>
              {item.effective_date}
            </SociollaText>
          </View>
        </View>
        <View style={{ flex: 1, marginLeft: 7 }}>
          <NoTranslate>
            <SociollaText
              style={[
                styles.statusBadgeMobile,
                { backgroundColor: item.statusColor },
              ]}>
              {item.status}
            </SociollaText>
          </NoTranslate>
        </View>
      </View>
    </TouchableOpacity>
  );

  // ── effects ──
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () => {
        const menu = [
          { type: 'props', name: 'Add', key: 'add' },
          { type: 'props', name: 'Filter', key: 'filter' },
          { type: 'props', name: 'Export', key: 'export' },
        ];
        if (!showAddEdit) {
          menu.splice(0, 1);
        }
        return isWeb ? (
          <NotificationSettingButton {...props} />
        ) : (
          <MoreButton menu={menu} callback={(value) => callback(value)} />
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, showAddEdit]);

  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      setDraftFilter(params.filter || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── render ──
  // Centered status badge; only the color is data-driven (from STATUS['price_rules']).
  const statusSlot = ({ data, index }) => (
    <View key={`status-${index}`} style={{ alignItems: 'center' }}>
      <View
        style={[styles.statusBadge, { backgroundColor: data?.statusColor }]}>
        <NoTranslate>
          <SociollaText style={styles.statusBadgeText}>
            {data?.status}
          </SociollaText>
        </NoTranslate>
      </View>
    </View>
  );

  return (
    <Container>
      {isWeb && (
        <View style={styles.headerContainer}>
          <SociollaText
            type="medium"
            color="#171D29"
            style={{ marginBottom: 40, fontSize: 24 }}>
            Pricelist
          </SociollaText>
          <View style={styles.actionContainer}>
            {showAddEdit && (
              <Button
                buttonStyle={{ ...buttonPrimary, ...buttonRounded, width: 110 }}
                title={
                  <SociollaText type="medium" style={styles.textAction}>
                    + Add
                  </SociollaText>
                }
                onPress={() => showDetail('add')}
              />
            )}
            <Button
              buttonStyle={{
                ...buttonPrimary,
                ...buttonRounded,
                width: 110,
                marginLeft: 15,
              }}
              title={
                <SociollaText type="medium" style={styles.textAction}>
                  Export
                </SociollaText>
              }
              onPress={() => exportPriceLogs()}
            />
          </View>
        </View>
      )}
      {isWeb ? (
        <Table
          filterName={scope}
          name="pricelist-screen"
          structures={tableStructure}
          data={rows}
          loading={list.isLoading}
          refetching={list.isFetching}
          hasCallback={true}
          hasNoHide={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          searchKey="description"
          searchValue={searchValue}
          searchPlaceholder="Search by description"
          onClickRow={({ data }) => callBackRowPress({ item: data })}
          slot={{ status: statusSlot }}
        />
      ) : (
        <TableMobile
          filterName={scope}
          name="pricelist-screen"
          searchKey="description"
          searchValue={searchValue}
          placeholder="Search by description"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          headerComponent={<></>}
          renderItem={renderItem}
        />
      )}
      <Layout.Filter
        name="pricelist-screen"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Effective Date
          </SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              label="Start Date"
              name="from_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: isWeb ? 'auto' : '50%',
              }}
              value={draftFilter.from_date?.$gte}
              onChange={(value) =>
                setDraftFilter((f) => ({
                  ...f,
                  from_date: { $gte: new Date(value).toISOString() },
                }))
              }
            />
            <DatePicker
              label="End Date"
              name="to_date"
              style={{ width: isWeb ? 'auto' : '50%' }}
              value={draftFilter.to_date?.$lte}
              disableBeforeDate={draftFilter.from_date?.$gte}
              onChange={(value) =>
                setDraftFilter((f) => ({
                  ...f,
                  to_date: { $lte: new Date(value).toISOString() },
                }))
              }
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <StatusPicker
            label="Status"
            options={getStatus('price_rules') || []}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="label"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem lastItem>
          <StatusPicker
            label="Channel"
            options={CHANNEL_OPTIONS}
            value={draftFilter['market_type.code']}
            trackBy="value"
            labelKey="label"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, 'market_type.code': val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>

      <ModalLoyaltySetup
        show={modalLoyalty}
        onClose={(reload) => onClose(reload)}
        isAddMode={isAddMode}
        id={id}
      />
      <ModalViewLoyalty
        show={modalView}
        data={selectedRow}
        onClose={() => modalViewLoyalty(false)}
        onEdit={(val) => onEdit(val)}
      />
      {pricelistUpload && (
        <ModalPricelistUpload
          navigation={props.navigation}
          show={pricelistUpload}
        />
      )}
      {showImportModalPricelist ? (
        <ModalUpload
          show={showImportModalPricelist}
          onClose={() =>
            dispatch(
              showPopup({ name: 'showImportModalPricelist', value: false }),
            )
          }
          templateUrl={importTemplate}
          onUpload={handleImport}
          autoUploadS3={true}
          name={'import_pricelist'}
          validHeader={[
            'description',
            'market_type',
            'currency',
            'taxes.type',
            'taxes.value',
            'valid_from',
            'valid_to',
            'reference',
            'price',
          ]}
        />
      ) : (
        false
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  actionContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  tableRow: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    minHeight: 72,
    width: '100%',
    flex: 1,
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingHorizontal: 20,
    paddingVertical: isMobileWeb ? 10 : 0,
  },
  textAction: {
    fontSize: 14,
    color: Colors.white,
    paddingHorizontal: 10,
  },
  statusBadge: {
    minWidth: 120,
    paddingHorizontal: 15,
    paddingVertical: 7,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusBadgeText: {
    fontSize: 11,
    textAlign: 'center',
    color: Colors.black,
  },
  statusBadgeMobile: {
    fontSize: 10,
    color: Colors.black,
    width: 120,
    paddingHorizontal: 15,
    paddingVertical: 7,
    borderRadius: 4,
    textAlign: 'center',
  },
  badgeContainer: {
    borderWidth: 1,
    borderColor: '#EA1A65',
    borderRadius: 15,
    paddingVertical: 4,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  badgeText: {
    color: '#D81B60',
    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'center',
  },
});

export default PricelistScreen;
